/**
 * Drives the theatrical typewriter reveal for the Vitalora killer
 * flow (3-asset parallel review).
 *
 * The killer flow lands as one synchronous payload from
 * `runKillerReviewFlow` — markdown, agentSteps, contentBlocks, and
 * projectData all arrive together. To preserve the demo's "reviewers
 * doing real work" effect, this hook paces those payloads out over
 * ~90 seconds in logical chunks (see `killerFlowStreaming.ts`).
 *
 * Behaviour:
 *
 * - Looks at the latest assistant message. Detects a freshly-landed
 *   killer-flow response by shape (has `contentBlocks` + at least
 *   one `tool_activity` block referencing >= 2 tool steps).
 * - Short-circuits if the message's `createdAt` is older than
 *   `staleThresholdMs`: history reload → render fully-revealed.
 * - Drives a chained `setTimeout` schedule (no `setInterval` —
 *   avoids stale-closure surprises in React). One timer per frame.
 * - Pauses when the tab goes hidden by deferring the next tick and
 *   re-scheduling against the original start time on visibility
 *   restore — so a prospect switching tabs doesn't lose pacing.
 * - Cleans up on unmount or message change.
 */

import {
  type AskPunttAgentStep,
  type AskPunttContentBlock,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  type KillerFlowFrame,
  planKillerFlowFrames,
  planKillerFlowTimings,
} from "../killerFlowStreaming";

export interface KillerFlowStreamResult {
  /** Message id currently being streamed; null if nothing is animating. */
  activeMessageId: string | null;
  /** Sliced contentBlocks to render for the active message. */
  revealedContentBlocks: AskPunttContentBlock[] | null;
  /** How many of the original agentSteps should be visible (slice 0..N). */
  revealedAgentStepCount: number | null;
  /** True while the typewriter is still running for the active message. */
  isStreaming: boolean;
}

interface UseKillerFlowStreamArgs {
  /**
   * Hard gate. When false, the hook is a no-op and returns the
   * empty state — this is the production safety net so non-demo
   * tenants never enter the theatrical path.
   */
  enabled: boolean;
  /** Snapshot of chat messages (newest last). */
  messages: AskPunttMessage[];
  /**
   * Total length of the typewriter reveal in milliseconds. The
   * default matches the product brief; tests override with smaller
   * values via fake timers.
   */
  totalDurationMs?: number;
  /**
   * Maximum age of a `createdAt` timestamp that still triggers the
   * theatrical reveal. Older messages (e.g. hydrated chat history)
   * land fully revealed.
   */
  staleThresholdMs?: number;
}

const DEFAULT_TOTAL_DURATION_MS = 88_000;
export const DEFAULT_STALE_THRESHOLD_MS = 60_000;

/**
 * True when a message is a freshly-arrived killer-flow assistant
 * response we should pace. The signal we lean on is the canonical
 * shape emitted by ``buildKillerContentBlocks`` PLUS the
 * killer-flow-only ``projectData`` payload — that combination is
 * what distinguishes a true killer-flow turn from any other
 * assistant message that happens to carry a ``tool_activity``
 * block of similar length.
 *
 * Historical bug: the predicate originally only checked the
 * tool_activity ``stepRange end - start >= 2`` shape. A single
 * ``compute_roi_layer`` tool call+result emits exactly that shape
 * (one ``tool_call`` step + one ``tool_result`` step → stepRange
 * ``[0, 2]``), so the rembrandt-side ROI answer for demo tenants
 * was getting misclassified as the 88-second killer-flow reveal.
 * Symptom: the dollar-figure text block landed correctly, then
 * the typewriter stripped contentBlocks back to the intro frame
 * and the ROI line "disappeared" for ~69s until the body window
 * came up.
 *
 * ``projectData`` is set by ``runKillerReviewFlow`` exclusively
 * (christo demo handler) and is not produced by ``compute_roi_layer``
 * nor by any live-AI rembrandt response, so requiring it here
 * tightens the predicate without changing real killer-flow detection.
 *
 * Exported so chat.tsx can detect "this message will trigger the
 * typewriter on the next render" — used by the single-ticket auto-
 * drill-in effect to avoid a race where ``activeProjectData`` lands
 * on render N but ``setActive()`` doesn't flush until render N+1.
 */
export function isKillerFlowAssistantMessage(msg: AskPunttMessage): boolean {
  if (msg.role !== "assistant") return false;
  if (msg.status === "pending") return false;
  if (!msg.contentBlocks?.length) return false;
  if (!msg.agentSteps?.length) return false;
  if (!msg.projectData) return false;
  const toolBlock = msg.contentBlocks.find((b) => b.type === "tool_activity");
  if (!toolBlock || toolBlock.type !== "tool_activity") return false;
  const [start, end] = toolBlock.stepRange;
  return end - start >= 2;
}

interface ActiveStream {
  messageId: string;
  startedAt: number;
  frames: KillerFlowFrame[];
  timings: number[];
}

export function useKillerFlowStream({
  enabled,
  messages,
  totalDurationMs = DEFAULT_TOTAL_DURATION_MS,
  staleThresholdMs = DEFAULT_STALE_THRESHOLD_MS,
}: UseKillerFlowStreamArgs): KillerFlowStreamResult {
  // Track which message ids we've already streamed (or skipped as
  // stale). Prevents re-running the typewriter when the chat polls
  // and re-emits the same message, or when an HMR reload bounces us
  // back into the component with the full payload already cached.
  const handledIdsRef = useRef<Set<string>>(new Set());
  // On the very first render, treat HISTORICAL messages as already
  // seen so re-mounting an old chat doesn't replay the typewriter.
  //
  // CRITICAL: only seed messages older than `staleThresholdMs`. A
  // freshly-created killer-flow chat lands here with the new
  // assistant message ALREADY populated in `localMessages` because
  // the send mutation primes the React Query cache before navigating
  // to /ask-puntt/:chatId — so by the time this hook mounts, the
  // killer-flow message is already at the tail. Seeding it as
  // "handled" makes the schedule never fire and the typewriter
  // collapses to an instant reveal (the bug Ronnie flagged: "way
  // faster than 90 seconds"). Using the staleness window as the
  // seed cutoff keeps history-replay protection but lets a fresh
  // killer-flow message fall through to `isKillerFlowAssistantMessage`
  // and start the schedule.
  const seededRef = useRef(false);
  if (!seededRef.current) {
    const now = Date.now();
    for (const m of messages) {
      const createdAtMs = m.createdAt ? Date.parse(m.createdAt) : NaN;
      const isFresh =
        !Number.isNaN(createdAtMs) && now - createdAtMs < staleThresholdMs;
      if (!isFresh) handledIdsRef.current.add(m._id);
    }
    seededRef.current = true;
  }

  const [active, setActive] = useState<ActiveStream | null>(null);
  const [frameIndex, setFrameIndex] = useState<number>(-1);
  const activeRef = useRef<ActiveStream | null>(null);
  activeRef.current = active;

  // Detect a freshly-arrived killer-flow message and kick off the
  // schedule. Re-runs on every messages change but uses the
  // `handledIdsRef` guard so the same message never starts twice.
  useEffect(() => {
    if (!enabled) return;
    const last = messages[messages.length - 1];
    if (!last) return;
    if (handledIdsRef.current.has(last._id)) return;
    if (!isKillerFlowAssistantMessage(last)) {
      // CRITICAL: do NOT mark `pending` assistant messages as handled.
      // The manual upload path lands a `status: "pending"` assistant
      // message first (no `contentBlocks` yet), then polling refetches
      // and the SAME `_id` is updated with the full killer-flow
      // payload. If we stamp the id into `handledIdsRef` during the
      // pending phase, the second pass (now killer-flow-shaped) hits
      // `handledIdsRef.has(last._id)` and bails — the schedule never
      // starts and the user sees the finished state instantly. Only
      // mark messages we're confident will never become killer-flow
      // (user messages, system messages, or assistant messages that
      // landed fully formed but with the wrong shape).
      if (last.role !== "assistant" || last.status !== "pending") {
        handledIdsRef.current.add(last._id);
      }
      return;
    }
    const createdAtMs = last.createdAt ? Date.parse(last.createdAt) : NaN;
    if (
      !Number.isNaN(createdAtMs) &&
      Date.now() - createdAtMs > staleThresholdMs
    ) {
      handledIdsRef.current.add(last._id);
      return;
    }
    handledIdsRef.current.add(last._id);
    const frames = planKillerFlowFrames(
      last.agentSteps as AskPunttAgentStep[],
      last.contentBlocks ?? undefined,
    );
    if (frames.length === 0) return;
    const timings = planKillerFlowTimings(frames, {
      totalDurationMs,
      messageIdSeed: last._id,
    });
    setActive({
      messageId: last._id,
      startedAt: Date.now(),
      frames,
      timings,
    });
    setFrameIndex(-1);
  }, [enabled, messages, totalDurationMs, staleThresholdMs]);

  // Drive the schedule with a chained setTimeout (per-frame, no
  // setInterval — keeps absolute-time semantics so tab throttling
  // self-corrects when the user returns).
  //
  // Tab visibility behaviour: when the tab goes hidden, browsers
  // throttle setTimeout to ~1s. We shift `startedAt` forward by the
  // time spent hidden so the schedule resumes from where it paused
  // rather than firing a burst of overdue frames on wake.
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let nextFrame = 0;
    let hiddenAt: number | null = null;

    const schedule = () => {
      if (cancelled) return;
      const cur = activeRef.current;
      if (!cur) return;
      if (nextFrame >= cur.timings.length) {
        setFrameIndex(cur.timings.length);
        return;
      }
      const target = cur.startedAt + cur.timings[nextFrame];
      const delay = Math.max(0, target - Date.now());
      timer = setTimeout(() => {
        if (cancelled) return;
        const c = activeRef.current;
        if (!c) return;
        if (document.visibilityState === "hidden") {
          // Don't advance while hidden — re-arm for the next tick
          // once we become visible. Browsers throttle hidden-tab
          // timers anyway; this just keeps the reveal aligned.
          timer = setTimeout(schedule, 1_000);
          return;
        }
        setFrameIndex(nextFrame);
        nextFrame += 1;
        schedule();
      }, delay);
    };

    const onVis = () => {
      const cur = activeRef.current;
      if (!cur) return;
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt != null) {
        // Shift the schedule's start forward by the time spent
        // hidden so the next frame fires the correct relative
        // time after visibility returns.
        const pausedFor = Date.now() - hiddenAt;
        hiddenAt = null;
        const shifted: ActiveStream = {
          ...cur,
          startedAt: cur.startedAt + pausedFor,
        };
        activeRef.current = shifted;
        setActive(shifted);
      }
    };

    document.addEventListener("visibilitychange", onVis);
    schedule();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
    // We intentionally key on `active.messageId` (not the whole
    // object) so the visibility-driven `startedAt` shift inside
    // setActive doesn't restart the schedule. The schedule reads
    // the latest startedAt via `activeRef`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.messageId]);

  // Eager-detect a freshly-arrived killer-flow message during render
  // and pre-compute frame 0 (intro line only). The useEffect above
  // is what eventually sets `active`, but it runs AFTER the first
  // commit — so on the frame a killer-flow message lands, without
  // this short-circuit the consumer would render the message's full
  // unstreamed `contentBlocks` + `agentSteps` (which falls through
  // to the finished markdown body in `computeDisplaySegments` when
  // contentBlocks is empty) for one tick before the schedule kicks
  // in. That's the "flash to finished state" the user reported.
  //
  // By computing frame 0 synchronously and returning it as the
  // pre-first-frame state, the assistant bubble shows ONLY the
  // "Spinning up Brand, Agency, Regulatory, and Medical reviewers…"
  // intro from the very first paint onwards.
  //
  // Mirrors the matching gates inside the useEffect so the two stay
  // in lockstep — message must be killer-flow shaped, unhandled, and
  // not stale.
  const pendingPrefirst = useMemo<{
    messageId: string;
    firstFrame: KillerFlowFrame;
  } | null>(() => {
    if (!enabled) return null;
    const last = messages[messages.length - 1];
    if (!last) return null;
    if (handledIdsRef.current.has(last._id)) return null;
    if (!isKillerFlowAssistantMessage(last)) return null;
    const createdAtMs = last.createdAt ? Date.parse(last.createdAt) : NaN;
    if (
      !Number.isNaN(createdAtMs) &&
      Date.now() - createdAtMs > staleThresholdMs
    ) {
      return null;
    }
    const frames = planKillerFlowFrames(
      last.agentSteps as AskPunttAgentStep[],
      last.contentBlocks ?? undefined,
    );
    if (frames.length === 0) return null;
    return { messageId: last._id, firstFrame: frames[0] };
  }, [enabled, messages, staleThresholdMs]);

  const result = useMemo<KillerFlowStreamResult>(() => {
    if (!active) {
      // No schedule running yet. If a fresh killer-flow message is
      // sitting at the tail awaiting the useEffect, paint frame 0
      // (intro line only) right now so we never flash the finished
      // markdown body.
      if (pendingPrefirst) {
        return {
          activeMessageId: pendingPrefirst.messageId,
          revealedContentBlocks: pendingPrefirst.firstFrame.contentBlocks,
          revealedAgentStepCount: pendingPrefirst.firstFrame.revealedStepCount,
          isStreaming: true,
        };
      }
      return {
        activeMessageId: null,
        revealedContentBlocks: null,
        revealedAgentStepCount: null,
        isStreaming: false,
      };
    }
    const total = active.frames.length;
    if (frameIndex < 0) {
      // Pre-first-frame inside the schedule: same as `pendingPrefirst`
      // — render frame 0 (intro line) so there's no gap between
      // detection and the schedule's first real tick.
      const f0 = active.frames[0];
      return {
        activeMessageId: active.messageId,
        revealedContentBlocks: f0?.contentBlocks ?? [],
        revealedAgentStepCount: f0?.revealedStepCount ?? 0,
        isStreaming: true,
      };
    }
    if (frameIndex >= total) {
      return {
        activeMessageId: active.messageId,
        revealedContentBlocks: null,
        revealedAgentStepCount: null,
        isStreaming: false,
      };
    }
    const f = active.frames[frameIndex];
    return {
      activeMessageId: active.messageId,
      revealedContentBlocks: f.contentBlocks,
      revealedAgentStepCount: f.revealedStepCount,
      isStreaming: true,
    };
  }, [active, frameIndex, pendingPrefirst]);

  return result;
}
